import React, { useEffect, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import api from '../api/api';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import { useData } from '../context/DataContext';

const CalendarPage = () => {
    const { t, i18n } = useTranslation();
    const { voitures: cars, fetchVoitures } = useData();
    const isRtl = i18n.language === 'ar';
    const [events, setEvents] = useState([]);
    const [filters, setFilters] = useState({ voiture_id: '', status: '' });
    const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

    useEffect(() => {
        fetchVoitures();
        fetchEvents();
        const onResize = () => setIsMobile(window.innerWidth < 640);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [fetchVoitures]);

    useEffect(() => {
        fetchEvents();
    }, [filters]);

    const fetchEvents = async () => {
        try {
            const res = await api.get('/reservations/calendar', {
                params: {
                    voiture_id: filters.voiture_id || undefined,
                    status: filters.status || undefined
                }
            });
            setEvents(res.data);
        } catch (err) {
            console.error('Failed to fetch reservations:', err);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    return (
        <Sidebar>
            {/* Mobile-friendly FullCalendar CSS overrides */}
            <style>{`
                .fc .fc-toolbar {
                    flex-wrap: wrap;
                    gap: 8px;
                }
                @media (max-width: 640px) {
                    .fc .fc-toolbar {
                        flex-direction: column;
                        align-items: center;
                    }
                    .fc .fc-toolbar-chunk {
                        display: flex;
                        flex-wrap: wrap;
                        justify-content: center;
                        gap: 4px;
                    }
                    .fc .fc-button {
                        padding: 4px 8px !important;
                        font-size: 0.72rem !important;
                    }
                    .fc .fc-toolbar-title {
                        font-size: 0.9rem !important;
                    }
                    .fc .fc-col-header-cell-cushion {
                        font-size: 0.68rem;
                        padding: 2px 2px;
                    }
                    .fc .fc-daygrid-day-number {
                        font-size: 0.72rem;
                        padding: 2px;
                    }
                    .fc .fc-event-title {
                        font-size: 0.68rem;
                    }
                    .fc td, .fc th {
                        padding: 0 !important;
                    }
                }
            `}</style>

            <div className="min-h-screen bg-gray-50 p-2 sm:p-6" dir={isRtl ? 'rtl' : 'ltr'}>
                <div className="max-w-7xl mx-auto">
                    <h1 className={`text-xl sm:text-3xl font-bold text-gray-800 mb-4 sm:mb-6 ${isRtl ? 'text-right' : 'text-left'}`}>
                        {t('reservation_calendar')}
                    </h1>

                    {/* Filter Section */}
                    <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
                        <h2 className={`text-base sm:text-lg font-semibold text-gray-700 mb-3 ${isRtl ? 'text-right' : 'text-left'}`}>
                            {t('filters')}
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            <div>
                                <label htmlFor="voiture_id" className="block text-sm font-medium text-gray-600 mb-1">
                                    Voiture
                                </label>
                                <select
                                    id="voiture_id"
                                    name="voiture_id"
                                    onChange={handleFilterChange}
                                    className="w-full p-2 sm:p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-sm"
                                >
                                    <option value="">Toutes les voitures</option>
                                    {cars.map((car) => (
                                        <option key={car.id} value={car.id}>
                                            {car.marque} {car.modele}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label htmlFor="status" className="block text-sm font-medium text-gray-600 mb-1">
                                    Statut
                                </label>
                                <select
                                    id="status"
                                    name="status"
                                    onChange={handleFilterChange}
                                    className="w-full p-2 sm:p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-sm"
                                >
                                    <option value="">Tous les statuts</option>
                                    <option value="en_attente">En attente</option>
                                    <option value="en_cours">En cours</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Calendar Section */}
                    <div className="bg-white rounded-xl shadow-md overflow-hidden p-1 sm:p-4">
                        <FullCalendar
                            plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
                            initialView={isMobile ? 'listMonth' : 'dayGridMonth'}
                            events={events}
                            height="auto"
                            headerToolbar={
                                isMobile
                                    ? {
                                        left: 'prev,next',
                                        center: 'title',
                                        right: 'dayGridMonth,listMonth'
                                    }
                                    : {
                                        left: 'prev,next today',
                                        center: 'title',
                                        right: 'dayGridMonth,dayGridWeek,timeGridDay,listMonth'
                                    }
                            }
                            eventDisplay="block"
                            eventColor="#4f46e5"
                            eventTextColor="#ffffff"
                            dayMaxEventRows={isMobile ? 2 : 3}
                            aspectRatio={isMobile ? 1.2 : 1.8}
                            locale="fr"
                            buttonText={{
                                today: isMobile ? "Auj." : "Aujourd'hui",
                                month: 'Mois',
                                week: 'Sem.',
                                day: 'Jour',
                                list: 'Liste'
                            }}
                            editable={true}
                            selectable={true}
                            weekends={true}
                            firstDay={1}
                        />
                    </div>
                </div>
            </div>
        </Sidebar>
    );
};

export default CalendarPage;